import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';

import { useAgencyLoginMutation } from '../slices/agencyAuthApiSlice';
import { setAgencyCredentials } from '../slices/authSlice';

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { agencyInfo } = useSelector((state) => state.agencyAuth);
  const [login, { isLoading, error }] = useAgencyLoginMutation();

  // Already logged in — don't show the login form again.
  if (agencyInfo) {
    const redirectTo = location.state?.from?.pathname || '/shifts/available';
    return <Navigate to={redirectTo} replace />;
  }

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      const res = await login({ email, password }).unwrap();
      dispatch(setAgencyCredentials(res));
      toast.success(`Welcome, ${res.name}`);
      const redirectTo = location.state?.from?.pathname || '/shifts/available';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err?.data?.error || err?.data?.message || 'Login failed');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
          Agency Login
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sign in with your agency account to view and grab available shifts.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.data?.error || error?.data?.message || 'Login failed'}
          </Alert>
        )}

        <Box component="form" onSubmit={submitHandler} noValidate>
          <TextField
            label="Email"
            type="email"
            fullWidth
            required
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            required
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 3 }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginScreen;
